import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { Suspense, lazy } from "react";

// Layout
import ProtectedRoute from "./ProtectedRoutes";
import AuthLayout from "../layouts/AuthLayout";
import Loader from "../components/Common/Loader";

// Pages
const LeadersHub = lazy(() => import("../pages/LeadersHub"));
const LogIn = lazy(() => import("../pages/Authentication/LogIn"));
const AdminLogIn = lazy(() => import("../pages/Authentication/LogIn/admin"));
const LogOut = lazy(() => import("../pages/Authentication/LogOut"));
const ResetPassword = lazy(() =>
  import("../pages/Authentication/ResetPassword")
);
const NotFound = lazy(() => import("../pages/404"));

// Pulse
const Mindfulness = lazy(() => import("../pages/Pulse/Mindfulness"));
const MindfulnessLeadingIssues = lazy(() =>
  import("../pages/Pulse/Mindfulness/Insights/LeadingIssues")
);
const MindfulnessPriorityIssues = lazy(() =>
  import("../pages/Pulse/Mindfulness/Insights/PriorityIssues")
);
const MindfulnessWellnessSupport = lazy(() =>
  import("../pages/Pulse/Mindfulness/Insights/WellnessSupport")
);
const MindfulnessWellnessInsights = lazy(() =>
  import("../pages/Pulse/Mindfulness/Insights/WellnessInsights")
);
const MindfulnessCompanyMood = lazy(() =>
  import("../pages/Pulse/Mindfulness/Insights/CompanyMood")
);
const MindfulnessActivityLevels = lazy(() =>
  import("../pages/Pulse/Mindfulness/Insights/ActivityLevels")
);
const MindfulnessConversational = lazy(() =>
  import("../pages/Pulse/Mindfulness/Insights/ConversationalAnalytics")
);
const MindfulnessWellnessCorrelation = lazy(() =>
  import("../pages/Pulse/Mindfulness/Insights/WellnessCorrelation")
);
const MindfulnessSatisfaction = lazy(() =>
  import("../pages/Pulse/Mindfulness/Insights/Satisfaction")
);
const MindfulnessProfessionals = lazy(() =>
  import("../pages/Pulse/Mindfulness/Insights/Professionals")
);

const PhysicalWellness = lazy(() => import("../pages/Pulse/Physical"));
const PhysicalWellnessInsights = lazy(() =>
  import("../pages/Pulse/Physical/Insights/PhysicalWellnessInsights")
);
const PhysicalWellnessWaterIntakeInsights = lazy(() =>
  import("../pages/Pulse/Physical/Insights/WaterTrackerInsights")
);
const PhysicalWellnessCalorieIntakeInsights = lazy(() =>
  import("../pages/Pulse/Physical/Insights/CalorieTrackerInsights")
);
const PhysicalWellnessSleepInsights = lazy(() =>
  import("../pages/Pulse/Physical/Insights/SleepTrackerInsights")
);
const PhysicalWellnessStepsInsights = lazy(() =>
  import("../pages/Pulse/Physical/Insights/StepsTrackerInsights")
);
const PhysicalWellnessActivityInsights = lazy(() =>
  import("../pages/Pulse/Physical/Insights/SportsActivityInsights")
);
const PhysicalWellnessHealthyInsights = lazy(() =>
  import("../pages/Pulse/Physical/Insights/PhysicalHealthyInsights")
);
const PhysicalWellnessHealthyMonthlyInsights = lazy(() =>
  import("../pages/Pulse/Physical/Insights/PhysicalHealthyMonthlyInsights")
);

const WomanWellness = lazy(() => import("../pages/Pulse/WomanWellness"));
const WomenWellnessLeadingIssues = lazy(() =>
  import("../pages/Pulse/WomanWellness/Insights/LeadingIssues")
);
const WomenWellnessPriorityIssues = lazy(() =>
  import("../pages/Pulse/WomanWellness/Insights/PriorityIssues")
);
const WomenWellnessSupport = lazy(() =>
  import("../pages/Pulse/WomanWellness/Insights/WellnessSupport")
);
const WomenWellnessInsights = lazy(() =>
  import("../pages/Pulse/WomanWellness/Insights/WellnessInsights")
);
const WomenWellnessCompanyMood = lazy(() =>
  import("../pages/Pulse/WomanWellness/Insights/CompanyMood")
);
const WomenWellnessActivityLevels = lazy(() =>
  import("../pages/Pulse/WomanWellness/Insights/ActivityLevels")
);
const WomenWellnessConversational = lazy(() =>
  import("../pages/Pulse/WomanWellness/Insights/ConversationalAnalytics")
);
const WomenWellnessCorrelation = lazy(() =>
  import("../pages/Pulse/WomanWellness/Insights/WellnessCorrelation")
);
const WomenWellnessSatisfaction = lazy(() =>
  import("../pages/Pulse/WomanWellness/Insights/Satisfaction")
);
const WomenWellnessProfessionals = lazy(() =>
  import("../pages/Pulse/WomanWellness/Insights/Professionals")
);

const HeartbeatIndex = lazy(() => import("../pages/Pulse/Heatbeat"));
const HeartbeatCreate = lazy(() => import("../pages/Pulse/Heatbeat/Create"));
const HeartbeatPreview = lazy(() =>
  import("../pages/Pulse/Heatbeat/SurveyPreview/SuveyPreviewDashboard")
);
const Heartbeat = lazy(() => import("../pages/Pulse/Heatbeat/Survey"));
const HeartbeatInsights = lazy(() =>
  import("../pages/Pulse/Heatbeat/Insights")
);
const HeartbeatTextAnalytics = lazy(() =>
  import("../pages/Pulse/Heatbeat/Insights/FreeTextAnalytics")
);

// Settings
const ProfileSettings = lazy(() => import("../pages/Settings/ProfileSettings"));
const ManagerUpload = lazy(() => import("../pages/Settings/ManagerUpload"));
const EmployeeUpload = lazy(() => import("../pages/Settings/EmployeeUpload"));

// Help & Support
const HelpAndSupport = lazy(() => import("../pages/Support"));

// Monitor
const Monitor = lazy(() => import("../pages/Monitor"));

const AppRoutes = () => {
  return (
    <Suspense fallback={<Loader />}>
      <Router>
        <Routes>
          {/* Protected Routes */}
          <Route element={<ProtectedRoute />}>
            <Route path="/leaders-hub" element={<LeadersHub />} />

            {/* Pulse */}
            <Route path="/pulse/mindfulness" element={<Mindfulness />} />
            <Route
              path="/pulse/mindfulness/leading-issues"
              element={<MindfulnessLeadingIssues />}
            />
            <Route
              path="/pulse/mindfulness/priority-issues"
              element={<MindfulnessPriorityIssues />}
            />
            <Route
              path="/pulse/mindfulness/wellness-correlation"
              element={<MindfulnessWellnessCorrelation />}
            />
            <Route
              path="/pulse/mindfulness/wellness-support"
              element={<MindfulnessWellnessSupport />}
            />
            <Route
              path="/pulse/mindfulness/professionals"
              element={<MindfulnessProfessionals />}
            />
            <Route
              path="/pulse/mindfulness/wellness-insights"
              element={<MindfulnessWellnessInsights />}
            />
            <Route
              path="/pulse/mindfulness/company-mood"
              element={<MindfulnessCompanyMood />}
            />
            <Route
              path="/pulse/mindfulness/activity-levels"
              element={<MindfulnessActivityLevels />}
            />
            <Route
              path="/pulse/mindfulness/satisfaction"
              element={<MindfulnessSatisfaction />}
            />
            <Route
              path="/pulse/mindfulness/conversational"
              element={<MindfulnessConversational />}
            />

            {/* physical */}
            <Route path="/pulse/physical" element={<PhysicalWellness />} />
            <Route
              path="/pulse/physical/wellness-insights"
              element={<PhysicalWellnessInsights />}
            />
            <Route
              path="/pulse/physical/water-intake-tracker"
              element={<PhysicalWellnessWaterIntakeInsights />}
            />
            <Route
              path="/pulse/physical/calorie-intake-tracker"
              element={<PhysicalWellnessCalorieIntakeInsights />}
            />
            <Route
              path="/pulse/physical/sleep-tracker"
              element={<PhysicalWellnessSleepInsights />}
            />
            <Route
              path="/pulse/physical/steps-tracker"
              element={<PhysicalWellnessStepsInsights />}
            />
            <Route
              path="/pulse/physical/sports-activity"
              element={<PhysicalWellnessActivityInsights />}
            />
            <Route
              path="/pulse/physical/healthy-employees"
              element={<PhysicalWellnessHealthyInsights />}
            />
            <Route
              path="/pulse/physical/physical-wellness-monthly"
              element={<PhysicalWellnessHealthyMonthlyInsights />}
            />

            {/* women wellness */}
            <Route path="/pulse/women-wellness" element={<WomanWellness />} />
            <Route
              path="/pulse/women-wellness/leading-issues"
              element={<WomenWellnessLeadingIssues />}
            />
            <Route
              path="/pulse/women-wellness/priority-issues"
              element={<WomenWellnessPriorityIssues />}
            />
            <Route
              path="/pulse/women-wellness/wellness-correlation"
              element={<WomenWellnessCorrelation />}
            />
            <Route
              path="/pulse/women-wellness/wellness-support"
              element={<WomenWellnessSupport />}
            />
            <Route
              path="/pulse/women-wellness/professionals"
              element={<WomenWellnessProfessionals />}
            />
            <Route
              path="/pulse/women-wellness/wellness-insights"
              element={<WomenWellnessInsights />}
            />
            <Route
              path="/pulse/women-wellness/company-mood"
              element={<WomenWellnessCompanyMood />}
            />
            <Route
              path="/pulse/women-wellness/activity-levels"
              element={<WomenWellnessActivityLevels />}
            />
            <Route
              path="/pulse/women-wellness/satisfaction"
              element={<WomenWellnessSatisfaction />}
            />
            <Route
              path="/pulse/women-wellness/conversational"
              element={<WomenWellnessConversational />}
            />

            {/* Heartbeats */}
            <Route path="/pulse/heartbeat" element={<HeartbeatIndex />} />
            <Route
              path="/pulse/heartbeat/create"
              element={<HeartbeatCreate />}
            />
            <Route
              path="/pulse/heartbeat/insights/:id"
              element={<HeartbeatInsights />}
            />
            <Route
              path="/pulse/heartbeat/insights/:id"
              element={<HeartbeatInsights />}
            />
            <Route
              path="/pulse/heartbeat/insights/freetext-analytics/:id/:country"
              element={<HeartbeatTextAnalytics />}
            />

            {/* Settings */}
            <Route
              path="/settings/profile-settings"
              element={<ProfileSettings />}
            />
            <Route
              path="/settings/manager-upload"
              element={<ManagerUpload />}
            />
            <Route
              path="/settings/employee-upload"
              element={<EmployeeUpload />}
            />

            {/* Help & Support */}
            <Route path="/support" element={<HelpAndSupport />} />

            {/* Monitor */}
            <Route path="/report" element={<Monitor />} />
          </Route>

          {/* Authentication Routes */}
          <Route element={<AuthLayout />}>
            <Route path="/login" element={<LogIn />} />
            <Route path="/admin-login" element={<AdminLogIn />} />
            <Route path="/logout" element={<LogOut />} />
            <Route path="/reset-password" element={<ResetPassword />} />

            <Route
              path="/pulse/heartbeat/preview/:id"
              element={<HeartbeatPreview />}
            />
            <Route path="/survey/submit/:id/:user" element={<Heartbeat />} />
          </Route>

          <Route path="/" element={<Navigate to="/leaders-hub" />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Router>
    </Suspense>
  );
};

export default AppRoutes;
